export const COMPOSER_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.txt',
  '.md',
  '.csv',
  '.tsv',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
] as const;

export const COMPOSER_ATTACHMENT_ACCEPT =
  COMPOSER_ATTACHMENT_EXTENSIONS.join(',');

export const COMPOSER_ATTACHMENT_MAX_FILES = 10;
export const COMPOSER_ATTACHMENT_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const COMPOSER_ATTACHMENT_MAX_TOTAL_BYTES = 30 * 1024 * 1024;

export const COMPOSER_ATTACHMENT_HELP =
  'Recruiting intake supports PDF, DOCX, TXT, MD, CSV, TSV, PNG, JPG, JPEG, and WEBP. You can send files without a message. Resume creation requires PDF or DOCX; tabular imports require CSV, TSV, or TXT. Audio and video are not supported. Up to 10 files, 10 MB each, 30 MB total.';

const ALLOWED_EXTENSION_SET = new Set<string>(COMPOSER_ATTACHMENT_EXTENSIONS);

export type ComposerAttachmentRejectionReason =
  | 'unsupported'
  | 'empty'
  | 'too_large'
  | 'duplicate'
  | 'too_many'
  | 'total_too_large';

export interface ComposerAttachmentRejection {
  file: File;
  reason: ComposerAttachmentRejectionReason;
  message: string;
}

export interface ComposerAttachmentSelection {
  accepted: File[];
  rejected: ComposerAttachmentRejection[];
}

const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
};

const fingerprint = (file: File): string =>
  `${file.name.trim().toLowerCase()}\u0000${file.size}\u0000${file.lastModified}`;

const rejection = (
  file: File,
  reason: ComposerAttachmentRejectionReason,
  message: string
): ComposerAttachmentRejection => ({ file, reason, message });

/**
 * Applies the AgentForge chat-composer attachment contract in selection order.
 * Valid files from a mixed batch are retained; every rejected file gets an
 * actionable message. This is UX validation only—the API remains responsible
 * for validating uploaded bytes.
 */
export const selectComposerAttachments = (
  existing: readonly File[],
  picked: readonly File[]
): ComposerAttachmentSelection => {
  const accepted: File[] = [];
  const rejected: ComposerAttachmentRejection[] = [];
  const fingerprints = new Set(existing.map(fingerprint));
  let fileCount = existing.length;
  let totalBytes = existing.reduce((total, file) => total + file.size, 0);

  for (const file of picked) {
    if (!ALLOWED_EXTENSION_SET.has(extensionOf(file.name))) {
      rejected.push(
        rejection(
          file,
          'unsupported',
          `${file.name} isn’t supported. Use PDF, DOCX, TXT, MD, CSV, TSV, PNG, JPG, JPEG, or WEBP.`
        )
      );
      continue;
    }

    if (file.size === 0) {
      rejected.push(
        rejection(file, 'empty', `${file.name} is empty and wasn’t attached.`)
      );
      continue;
    }

    if (file.size > COMPOSER_ATTACHMENT_MAX_FILE_BYTES) {
      rejected.push(
        rejection(
          file,
          'too_large',
          `${file.name} is over the 10 MB per-file limit and wasn’t attached.`
        )
      );
      continue;
    }

    const key = fingerprint(file);
    if (fingerprints.has(key)) {
      rejected.push(
        rejection(file, 'duplicate', `${file.name} is already attached.`)
      );
      continue;
    }

    if (fileCount >= COMPOSER_ATTACHMENT_MAX_FILES) {
      rejected.push(
        rejection(
          file,
          'too_many',
          `Only 10 files can be attached. ${file.name} wasn’t attached.`
        )
      );
      continue;
    }

    if (totalBytes + file.size > COMPOSER_ATTACHMENT_MAX_TOTAL_BYTES) {
      rejected.push(
        rejection(
          file,
          'total_too_large',
          `Adding ${file.name} would exceed the 30 MB total limit.`
        )
      );
      continue;
    }

    accepted.push(file);
    fingerprints.add(key);
    fileCount += 1;
    totalBytes += file.size;
  }

  return { accepted, rejected };
};
